import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Alert from "../../../Common/Alert";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import {
  ChartOfAccountStyles,
  formatDate,
  money,
  num,
} from "../../Accounts/Chart-Of-Account/ChartOfAccountShared";
import {
  FinancialStatementStyles,
  PresetChips,
  periodPresets,
  printStatement,
} from "./FinancialStatementShared";

const TITLE = "Distributors PS Within Date Range";
const UNASSIGNED = "Unassigned";

const GROUPS = [
  ["", "No grouping"],
  ["region", "Region"],
  ["area", "Area"],
  ["base", "Base"],
  ["distributor", "Distributor"],
];

const place = (value) => value || UNASSIGNED;

const groupLabel = (row, by) =>
  by === "distributor"
    ? `${row.distributor_code} - ${row.distributor_name}`
    : place(row[by]);

const csvCell = (value) => {
  const text = value === null || value === undefined ? "" : String(value);
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const DpsStyles = () => (
  <style>
    {`
      .dps-table { width: 100%; margin: 0; border-collapse: collapse; }
      .dps-table thead th {
        padding: 10px 12px;
        background: #2a2962;
        color: #fff;
        font-size: 0.7rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.04em;
        white-space: nowrap;
        border: 0;
      }
      .dps-table thead th.coa-num { text-align: right; }
      .dps-table td {
        padding: 8px 12px;
        border-bottom: 1px solid #f0f1f6;
        font-size: 0.85rem;
        vertical-align: middle;
      }
      .dps-table td.coa-num { white-space: nowrap; }
      .dps-table tbody tr.dps-row:hover td { background: #fafbfd; }
      .dps-sl { width: 48px; color: #8b90a5; font-size: 0.75rem; }
      .dps-unassigned { color: #b6bacb; font-style: italic; }
      .dps-group td {
        background: #f7f8fc;
        font-weight: 700;
        color: #2a2962;
      }
      .dps-group .dps-count {
        margin-left: 8px;
        font-size: 0.72rem;
        font-weight: 600;
        color: #8b90a5;
      }
      .dps-subtotal td {
        font-weight: 700;
        color: #2a2962;
        border-top: 1px solid #b6bacb;
      }
      .dps-table tfoot td {
        padding: 11px 12px;
        background: #2a2962;
        color: #fff;
        font-weight: 700;
      }
      .dps-voucher { color: #2a2962; font-weight: 600; }
      .dps-voucher:hover { color: #e42320; text-decoration: underline; }
      .dps-type {
        display: block;
        font-size: 0.68rem;
        color: #8b90a5;
        text-transform: capitalize;
      }
      .dps-filters .form-control { font-size: 0.82rem; }
      .dps-search { position: relative; }
      .dps-search i {
        position: absolute; top: 50%; left: 10px;
        transform: translateY(-50%); color: #b6bacb;
      }
      .dps-search .form-control { padding-left: 30px; }
    `}
  </style>
);

const Place = ({ value }) =>
  value ? <span>{value}</span> : <span className="dps-unassigned">{UNASSIGNED}</span>;

const DistributorsPsWithinDateRange = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [, thisMonthFrom, thisMonthTo] = periodPresets()[0];
  const [inputs, setInputs] = useState({
    start_date: searchParams.get("start_date") || thisMonthFrom,
    end_date: searchParams.get("end_date") || thisMonthTo,
  });
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState(null);
  const [filters, setFilters] = useState({
    region: "",
    area: "",
    base: "",
    search: "",
  });
  const [groupBy, setGroupBy] = useState("");

  const fetchData = async (params = inputs) => {
    setLoading(true);
    setAlert(null);

    const query = { start_date: params.start_date, end_date: params.end_date };
    setSearchParams(query, { replace: true });

    try {
      const res = await Http.get("/distributors-ps-within-date-range", {
        params: query,
      });
      setData(res.data);
    } catch (error) {
      console.error("Fetch error:", error);
      setData(null);
      setAlert({
        message:
          error.response?.data?.message ||
          "Could not load the report. Please try again.",
        type: "danger",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setInputs((values) => ({ ...values, [name]: value }));
  };

  const apply = (changes) => {
    const next = { ...inputs, ...changes };
    setInputs(next);
    fetchData(next);
  };

  const setFilter = (name, value) =>
    setFilters((values) => {
      const next = { ...values, [name]: value };
      // Narrowing a level clears the levels below it.
      if (name === "region") {
        next.area = "";
        next.base = "";
      }
      if (name === "area") {
        next.base = "";
      }
      return next;
    });

  const allRows = data ? data.rows : [];

  // Filter options come from the rows themselves, each level narrowed by the
  // one above it.
  const options = useMemo(() => {
    const unique = (list) => [...new Set(list)].sort((a, b) => a.localeCompare(b));
    const inRegion = allRows.filter(
      (row) => !filters.region || place(row.region) === filters.region,
    );
    const inArea = inRegion.filter(
      (row) => !filters.area || place(row.area) === filters.area,
    );
    return {
      region: unique(allRows.map((row) => place(row.region))),
      area: unique(inRegion.map((row) => place(row.area))),
      base: unique(inArea.map((row) => place(row.base))),
    };
  }, [allRows, filters.region, filters.area]);

  const rows = useMemo(() => {
    const query = filters.search.trim().toLowerCase();
    return allRows.filter(
      (row) =>
        (!filters.region || place(row.region) === filters.region) &&
        (!filters.area || place(row.area) === filters.area) &&
        (!filters.base || place(row.base) === filters.base) &&
        (!query ||
          `${row.distributor_code} ${row.distributor_name} ${row.voucher_no || ""}`
            .toLowerCase()
            .includes(query)),
    );
  }, [allRows, filters]);

  // Rows are already sorted by region, area, base, distributor and date, so
  // a group is a run of consecutive rows with the same label.
  const groups = useMemo(() => {
    if (!groupBy) {
      return [{ key: "all", label: null, rows }];
    }
    const list = [];
    rows.forEach((row) => {
      const label = groupLabel(row, groupBy);
      const last = list[list.length - 1];
      if (last && last.label === label) {
        last.rows.push(row);
      } else {
        list.push({ key: `${groupBy}-${label}-${list.length}`, label, rows: [row] });
      }
    });
    return list;
  }, [rows, groupBy]);

  const total = rows.reduce((sum, row) => sum + num(row.amount), 0);
  const distributorCount = new Set(rows.map((row) => row.distributor_id)).size;
  const unassignedCount = new Set(
    rows.filter((row) => !row.base).map((row) => row.distributor_id),
  ).size;

  const topRegion = useMemo(() => {
    const sums = {};
    rows.forEach((row) => {
      const key = place(row.region);
      sums[key] = (sums[key] || 0) + num(row.amount);
    });
    const [name, amount] =
      Object.entries(sums).sort((a, b) => b[1] - a[1])[0] || [];
    return name ? { name, amount } : null;
  }, [rows]);

  const periodLabel = data
    ? `${formatDate(data.start_date)} – ${formatDate(data.end_date)}`
    : "";

  const accountLabel = data
    ? data.accounts.map((a) => `${a.code} - ${a.name}`).join(", ")
    : "";

  const filterSummary = [
    filters.region && `Region: ${filters.region}`,
    filters.area && `Area: ${filters.area}`,
    filters.base && `Base: ${filters.base}`,
    filters.search.trim() && `Search: "${filters.search.trim()}"`,
  ]
    .filter(Boolean)
    .join(" · ");

  const exportCsv = () => {
    const header = [
      "Sl", "Region", "Area", "Base", "Distributor Code",
      "Distributor Name", "Date", "Voucher No", "Voucher Type", "Amount",
    ];
    const lines = [
      [TITLE],
      [`Period: ${data.start_date} to ${data.end_date}`],
      ...(filterSummary ? [[filterSummary]] : []),
      [],
      header,
      ...rows.map((row, i) => [
        i + 1,
        place(row.region),
        place(row.area),
        place(row.base),
        row.distributor_code,
        row.distributor_name,
        row.date,
        row.voucher_no,
        row.voucher_type,
        num(row.amount).toFixed(2),
      ]),
      [],
      ["", "", "", "", "", "", "", "", "Total", total.toFixed(2)],
    ];
    const csv = lines.map((line) => line.map(csvCell).join(",")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `distributors-ps_${data.start_date}_${data.end_date}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const handlePrint = () => {
    const headHtml = `
      <th>Sl</th><th>Region</th><th>Area</th><th>Base</th>
      <th>Code</th><th>Distributor</th><th>Date</th><th>Voucher</th>
      <th class="num">Amount</th>`;

    let serial = 0;
    const bodyHtml =
      groups
        .map((group) => {
          const head = group.label
            ? `<tr class="group"><td colspan="9">${group.label}</td></tr>`
            : "";
          const body = group.rows
            .map((row) => {
              serial += 1;
              return `
                <tr>
                  <td>${serial}</td>
                  <td>${place(row.region)}</td>
                  <td>${place(row.area)}</td>
                  <td>${place(row.base)}</td>
                  <td class="code">${row.distributor_code || ""}</td>
                  <td>${row.distributor_name || ""}</td>
                  <td>${formatDate(row.date)}</td>
                  <td>${row.voucher_no || ""}</td>
                  <td class="num">${money(row.amount)}</td>
                </tr>`;
            })
            .join("");
          const foot = group.label
            ? `<tr class="subtotal"><td colspan="8">Total ${group.label}</td><td class="num">${money(
                group.rows.reduce((sum, row) => sum + num(row.amount), 0),
              )}</td></tr>`
            : "";
          return head + body + foot;
        })
        .join("") +
      `<tr class="grand"><td colspan="8">Total Received</td><td class="num">${money(total)}</td></tr>`;

    printStatement({
      title: "Distributor Primary Sales Amount Received",
      subtitle: `${periodLabel}${filterSummary ? ` &middot; ${filterSummary}` : ""} &middot; Amounts in Taka`,
      headHtml,
      bodyHtml,
      note: `Credits to ${accountLabel} against distributors, dated within the period.`,
    });
  };

  const stat = (label, icon, value, sub) => (
    <div className="col-md-3 mg-b-10">
      <div className="coa-stat">
        <i className={`fa ${icon} coa-stat-icon`}></i>
        <span className="coa-stat-label">{label}</span>
        <span className="coa-stat-value">{value}</span>
        <span className="coa-stat-sub">{sub}</span>
      </div>
    </div>
  );

  let serial = 0;

  return (
    <div>
      <IsLogin></IsLogin>
      <div className="am-mainpanel">
        <ChartOfAccountStyles />
        <FinancialStatementStyles />
        <DpsStyles />
        <div className="am-pagetitle">
          <div>
            <h5 className="am-title mb-1">{TITLE}</h5>
            <p className="text-muted mb-0 tx-12">
              Primary sales amounts received from distributors
              {data && `, ${periodLabel}`}
            </p>
          </div>
          <div className="d-flex gap-2 coa-no-print">
            <Link to="/reports" className="btn btn-outline-secondary">
              <i className="fa fa-arrow-left mg-r-6"></i>Reports
            </Link>
            <button
              className="btn btn-outline-secondary"
              onClick={() => fetchData()}
              title="Reload data"
              disabled={loading}
            >
              <i className="fa fa-refresh"></i>
            </button>
            <button
              className="btn btn-outline-secondary"
              onClick={exportCsv}
              disabled={loading || !rows.length}
            >
              <i className="fa fa-file-excel-o mg-r-6"></i>Export CSV
            </button>
            <button
              className="btn btn-primary"
              onClick={handlePrint}
              disabled={loading || !data}
            >
              <i className="fa fa-print mg-r-6"></i>Print
            </button>
          </div>
        </div>

        <div className="am-pagebody">
          <Alert alert={alert} />

          <div className="coa-panel coa-no-print">
            <div className="coa-panel-head">
              <span>
                <i className="fa fa-calendar-o me-2"></i>Receipt Date
              </span>
              {data && (
                <span className="coa-head-note">
                  Credits to {accountLabel}
                </span>
              )}
            </div>
            <div className="coa-panel-body">
              <div className="row align-items-start">
                <div className="col-lg-6">
                  <label>Period</label>
                  <div className="d-flex gap-2">
                    <input
                      type="date"
                      className="form-control"
                      name="start_date"
                      value={inputs.start_date}
                      onChange={handleChange}
                      max={inputs.end_date}
                      aria-label="Start date"
                    />
                    <input
                      type="date"
                      className="form-control"
                      name="end_date"
                      value={inputs.end_date}
                      onChange={handleChange}
                      min={inputs.start_date}
                      aria-label="End date"
                    />
                  </div>
                  <PresetChips
                    presets={periodPresets().slice(0, 4)}
                    isActive={([, from, to]) =>
                      inputs.start_date === from && inputs.end_date === to
                    }
                    onPick={([, from, to]) =>
                      apply({ start_date: from, end_date: to })
                    }
                  />
                </div>
                <div className="col-lg-2 mg-t-10 mg-lg-t-0">
                  <label>&nbsp;</label>
                  <button
                    className="btn btn-primary"
                    onClick={() => fetchData()}
                    disabled={loading || !inputs.start_date || !inputs.end_date}
                  >
                    <i
                      className={`fa ${loading ? "fa-spinner fa-spin" : "fa-search"} mg-r-6`}
                    ></i>
                    {loading ? "Loading..." : "Apply"}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {data && (
            <div className="row mg-b-20">
              {stat(
                "Total Received",
                "fa-money",
                money(total),
                `${rows.length} receipt${rows.length === 1 ? "" : "s"}`,
              )}
              {stat(
                "Distributors",
                "fa-truck",
                distributorCount,
                distributorCount
                  ? `Average ${money(total / distributorCount)} each`
                  : "No receipts in the period",
              )}
              {stat(
                "Top Region",
                "fa-map-marker",
                topRegion ? topRegion.name : "–",
                topRegion ? money(topRegion.amount) : "No receipts in the period",
              )}
              {stat(
                "Average Receipt",
                "fa-file-text-o",
                rows.length ? money(total / rows.length) : "–",
                periodLabel,
              )}
            </div>
          )}

          {data && unassignedCount > 0 && (
            <div className="coa-notice">
              <i className="fa fa-info-circle"></i>
              <span>
                {unassignedCount} distributor{unassignedCount === 1 ? " has" : "s have"}{" "}
                no base set up in Workplace, so region, area and base show as{" "}
                <em>{UNASSIGNED}</em>.
              </span>
            </div>
          )}

          <div className="coa-panel">
            <div className="coa-panel-head">
              <span>
                <i className="fa fa-table me-2"></i>Distributor Receipts
                {filterSummary && (
                  <span className="coa-head-note mg-l-10">{filterSummary}</span>
                )}
              </span>
              {(filters.region || filters.area || filters.base || filters.search) && (
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm coa-no-print"
                  onClick={() =>
                    setFilters({ region: "", area: "", base: "", search: "" })
                  }
                >
                  <i className="fa fa-times mg-r-6"></i>Clear filters
                </button>
              )}
            </div>

            {data && allRows.length > 0 && (
              <div className="coa-panel-body dps-filters coa-no-print pd-b-0">
                <div className="row">
                  {["region", "area", "base"].map((level) => (
                    <div className="col-lg-2 col-md-4 mg-b-10" key={level}>
                      <label>{level}</label>
                      <select
                        className="form-control"
                        value={filters[level]}
                        onChange={(e) => setFilter(level, e.target.value)}
                      >
                        <option value="">All</option>
                        {options[level].map((value) => (
                          <option key={value} value={value}>
                            {value}
                          </option>
                        ))}
                      </select>
                    </div>
                  ))}
                  <div className="col-lg-2 col-md-4 mg-b-10">
                    <label>Group by</label>
                    <select
                      className="form-control"
                      value={groupBy}
                      onChange={(e) => setGroupBy(e.target.value)}
                    >
                      {GROUPS.map(([value, label]) => (
                        <option key={value || "none"} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="col-lg-4 col-md-8 mg-b-10">
                    <label>Search</label>
                    <div className="dps-search">
                      <i className="fa fa-search" aria-hidden="true"></i>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Distributor code, name or voucher no"
                        value={filters.search}
                        onChange={(e) => setFilter("search", e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}

            {loading && !data ? (
              <div className="pd-y-10">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="fs-skeleton"></div>
                ))}
              </div>
            ) : !data ? (
              <div className="coa-empty">
                <i className="fa fa-exclamation-circle"></i>
                The report could not be loaded.
              </div>
            ) : !rows.length ? (
              <div className="coa-empty">
                <i className="fa fa-inbox"></i>
                {allRows.length
                  ? "No receipts match the filters."
                  : `No amounts were received from distributors between ${periodLabel}.`}
              </div>
            ) : (
              <div className="table-responsive">
                <table className="dps-table">
                  <thead>
                    <tr>
                      <th>Sl</th>
                      <th>Region</th>
                      <th>Area</th>
                      <th>Base</th>
                      <th>Code</th>
                      <th>Distributor</th>
                      <th>Date</th>
                      <th>Voucher</th>
                      <th className="coa-num">Amount</th>
                    </tr>
                  </thead>
                  <tbody style={{ opacity: loading ? 0.5 : 1 }}>
                    {groups.map((group) => (
                      <React.Fragment key={group.key}>
                        {group.label && (
                          <tr className="dps-group">
                            <td colSpan={9}>
                              {group.label}
                              <span className="dps-count">
                                {group.rows.length} receipt
                                {group.rows.length === 1 ? "" : "s"}
                              </span>
                            </td>
                          </tr>
                        )}
                        {group.rows.map((row) => {
                          serial += 1;
                          return (
                            <tr key={row.id} className="dps-row">
                              <td className="dps-sl">{serial}</td>
                              <td><Place value={row.region} /></td>
                              <td><Place value={row.area} /></td>
                              <td><Place value={row.base} /></td>
                              <td>
                                <span className="coa-code">
                                  {row.distributor_code}
                                </span>
                              </td>
                              <td style={{ fontWeight: 600, color: "#2a2962" }}>
                                {row.distributor_name}
                              </td>
                              <td style={{ whiteSpace: "nowrap" }}>
                                {formatDate(row.date)}
                              </td>
                              <td>
                                <Link
                                  to={`/journal-voucher-details/${row.voucher_id}`}
                                  className="dps-voucher"
                                  title="Open the voucher"
                                >
                                  {row.voucher_no}
                                </Link>
                                <span className="dps-type">
                                  {row.voucher_type}
                                  {row.account_code && ` · ${row.account_code}`}
                                </span>
                              </td>
                              <td
                                className="coa-num"
                                style={{ fontWeight: 700, color: "#2a2962" }}
                              >
                                {money(row.amount)}
                              </td>
                            </tr>
                          );
                        })}
                        {group.label && (
                          <tr className="dps-subtotal">
                            <td colSpan={8} style={{ textAlign: "right" }}>
                              Total {group.label}
                            </td>
                            <td className="coa-num">
                              {money(
                                group.rows.reduce(
                                  (sum, row) => sum + num(row.amount),
                                  0,
                                ),
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan={8} style={{ textAlign: "right" }}>
                        Total Received
                      </td>
                      <td className="coa-num">{money(total)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DistributorsPsWithinDateRange;
